import { useState, useEffect, useCallback, useRef } from 'react'
import { Card, CardContent } from '../../../components/ui/Card'
import { Button } from '../../../components/ui/Button'
import InlineLoader from '../../../components/ui/InlineLoader'
import ViewSaleModal from '../../../components/modals/ViewSaleModal'
import AuthService from '../../../services/authService'
import BranchService from '../../../services/branchService'
import SalesService from '../../../services/salesService'
import { 
  RefreshCw, 
  Download,
  DollarSign,
  TrendingUp,
  ShoppingCart,
  Users,
  AlertTriangle,
  Search,
  Eye,
  Calendar,
  ChevronDown,
  Printer,
  FileText,
  Globe,
  Filter,
  X
} from 'lucide-react'
import { SalesModel } from '../../../utils/salesModel'
import { EnhancedReceiptGenerator } from '../../../utils/enhancedReceiptGenerator'
import { ReceiptGenerator } from '../../../utils/receiptGenerator'
import { HTMLReceiptGenerator } from '../../../utils/htmlReceiptGenerator'
import { SimpleItemEnhancer } from '../../../utils/simpleItemEnhancer'
import { formatCurrency } from '../../../utils/formatters'
import returnService from '../../../services/returnService'

// Utility function to convert order types for API
const convertOrderTypeForAPI = (orderType) => {
  const typeMap = {
    'dine-in': 'dine_in',
    'takeaway': 'take_away',
    'delivery': 'delivery',
    'online': 'online'
  }
  return typeMap[orderType] || orderType
}

const Toast = ({ message, type, onClose }) => {
  const getToastStyles = () => {
    switch(type) {
      case 'success': return 'bg-green-500 text-white'
      case 'error': return 'bg-red-500 text-white'
      case 'info': return 'bg-blue-500 text-white'
      default: return 'bg-gray-500 text-white'
    }
  }

  useEffect(() => {
    const timer = setTimeout(onClose, 5000)
    return () => clearTimeout(timer)
  }, [onClose])

  return (
    <div className={`fixed top-4 right-4 p-4 rounded-md shadow-lg z-50 ${getToastStyles()}`}>
      <div className="flex items-center justify-between">
        <span>{message}</span>
        <button onClick={onClose} className="ml-4 text-white hover:text-gray-200">×</button>
      </div>
    </div>
  )
}

export const Sales = () => {
  const currentUser = AuthService.getCurrentUser()
  const userBranchId = currentUser?.branchId
  const userRole = currentUser?.role?.toLowerCase()

  // Role flags
  const isSuperAdmin = userRole === 'super_admin'
  const isAdmin      = userRole === 'admin'
  const isManager    = userRole === 'manager'
  // Admin and super_admin can see all branches — no forced branch filter
  const canViewAllBranches = isSuperAdmin || isAdmin
  // Managers/cashiers are locked to their own branch
  const isBranchLocked = !canViewAllBranches && !!userBranchId

  // Use ref to track search timeout to prevent cursor jumping
  const searchTimeoutRef = useRef(null)

  const [sales, setSales] = useState([])
  const [branches, setBranches] = useState([])
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [actionLoading, setActionLoading] = useState({})
  const [error, setError] = useState(null)
  const [toast, setToast] = useState(null)

  const [searchTerm, setSearchTerm] = useState('')
  // Admin/super_admin: no default branch (see all). Manager/cashier: locked to their branch.
  const [branchFilter, setBranchFilter] = useState(canViewAllBranches ? '' : (userBranchId || ''))
  const [orderTypeFilter, setOrderTypeFilter] = useState('')
  const [dateFromFilter, setDateFromFilter] = useState('')

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(25)

  // Reset pagination when filters change
  useEffect(() => {
    setCurrentPage(1)
  }, [searchTerm, branchFilter, orderTypeFilter, dateFromFilter])

  const [showViewModal, setShowViewModal] = useState(false)
  const [selectedSale, setSelectedSale] = useState(null)
  const [dropdownOpen, setDropdownOpen] = useState({})
  const [selectedReturnView, setSelectedReturnView] = useState(null)

  const [stats, setStats] = useState({
    totalSales: 0,
    totalAmount: 0,
    totalReturns: 0,
    netSales: 0,
    avgOrderValue: 0,
    totalOrders: 0
  })

  const showToast = useCallback((message, type) => {
    setToast({ message, type })
    setTimeout(() => setToast(null), 5000)
  }, [])

  const loadBranches = useCallback(async () => {
    try {
      const response = await BranchService.getAllBranches()
      let branchesData = []
      
      if (response.data?.branches) branchesData = response.data.branches
      else if (response.branches) branchesData = response.branches
      else if (Array.isArray(response.data)) branchesData = response.data
      else if (Array.isArray(response)) branchesData = response
      
      setBranches(Array.isArray(branchesData) ? branchesData : [])
    } catch (error) {
      console.error('Failed to load branches:', error)
      setBranches([])
    }
  }, [])

  // Use refs to store current filter values to avoid dependency issues
  const filtersRef = useRef({
    searchTerm: '',
    branchFilter: canViewAllBranches ? '' : (userBranchId || ''),
    orderTypeFilter: '',
    dateFromFilter: ''
  })

  // Update refs whenever filters change
  useEffect(() => {
    filtersRef.current = {
      searchTerm,
      branchFilter,
      orderTypeFilter,
      dateFromFilter
    }
  }, [searchTerm, branchFilter, orderTypeFilter, dateFromFilter])

  const loadSales = useCallback(async (params = {}) => {
    try {
      const filterParams = { ...params }
      const currentFilters = filtersRef.current
      const activeBranchId = isBranchLocked
        ? userBranchId
        : (currentFilters.branchFilter || null)

      // Branch filter logic:
      // - Manager/cashier: always locked to their own branch
      // - Admin/super_admin: use whatever branch they've selected in the dropdown (empty = all)
      if (activeBranchId) filterParams.branch = activeBranchId
      // If canViewAllBranches and branchFilter is empty — no branch param = API returns all branches

      if (currentFilters.searchTerm) filterParams.search = currentFilters.searchTerm
      if (currentFilters.orderTypeFilter) filterParams.orderType = convertOrderTypeForAPI(currentFilters.orderTypeFilter)
      if (currentFilters.dateFromFilter) filterParams.dateFrom = currentFilters.dateFromFilter

      console.log('Loading sales with params:', filterParams)

      const response = await SalesService.getAllSales(filterParams)
      const parsedData = SalesService.parseResponse(response)

      if (parsedData.sales) {
        const formattedSales = parsedData.sales.map(sale => SalesModel.formatSaleForDisplay(sale))
        setSales(formattedSales)

        // ── Fetch returns (no backend branch filter — filter frontend) ──────────
        let returnRows = []
        try {
          const returnsResp = await returnService.getAllOrderReturns()
          const allReturns  = returnsResp?.data?.orderReturns || returnsResp?.data?.returns || []

          // Frontend branch + date filter
          const filteredReturns = allReturns.filter(r => {
            const matchBranch = !activeBranchId ||
              [r.order?.branch?._id, r.order?.branch, r.branch?._id, r.branch, r.order?.branchId, r.branchId]
                .some(c => c && String(c) === String(activeBranchId))

            // Date filter: return date must be on or after dateFromFilter
            let matchDate = true
            if (currentFilters.dateFromFilter) {
              const returnDate = new Date(r.createdAt || r.returnDate)
              const filterDate = new Date(currentFilters.dateFromFilter)
              filterDate.setHours(0, 0, 0, 0)
              matchDate = returnDate >= filterDate
            }
            return matchBranch && matchDate
          })

          returnRows = filteredReturns.map(r => {
            let returnItemsAmt = 0
            let returnedSC = 0
            let returnDateVal = r.createdAt || r.returnDate

            if (r.refundedServiceFee !== undefined && r.refundedServiceFee > 0) {
              returnedSC = parseFloat(r.refundedServiceFee)
              returnItemsAmt = parseFloat(r.totalAmount || 0) - returnedSC
            } else {
              const orderSC  = parseFloat(r.order?.fees?.serviceFeeAmount || 0)
              const baseAmt  = parseFloat(r.order?.fees?.baseAmountAfterPromotions || r.order?.orderPayment?.totalAmount || 0)
              const returnAmt = parseFloat(r.totalAmount || 0)
              
              returnedSC = baseAmt > 0 ? (returnAmt / baseAmt) * orderSC : 0
              returnItemsAmt = returnAmt
            }

            // Chronological check: If return time appears before order time, it must be in UTC.
            // Adjust return date by adding 5.5 hours timezone offset (GMT+5:30)
            const orderDateVal = new Date(r.order?.orderDate || r.order?.createdAt)
            if (returnDateVal && !isNaN(orderDateVal.getTime())) {
              const rDate = new Date(returnDateVal)
              if (rDate < orderDateVal) {
                const adjusted = new Date(rDate.getTime() + 5.5 * 60 * 60 * 1000)
                returnDateVal = adjusted.toISOString()
              }
            }

            const displayTotal = -(returnItemsAmt + returnedSC)

            return {
              id:           `return-${r._id}`,
              _id:          `return-${r._id}`,
              orderNumber:  r.order?.orderNumber || r.orderNumber || r._id?.slice(-8) || 'N/A',
              branchName:   r.order?.branch?.name || '',
              customerName: r.order?.customer?.name || 'Walk-in',
              customerMobile: '',
              orderType:    'return',
              orderTypeConfig: {
                label: 'Return',
                badgeClass: 'bg-red-100 text-red-700'
              },
              itemsCount:   r.items?.reduce((s, i) => s + (i.returnQuantity || 1), 0) || 0,
              discount:     0,
              total:        displayTotal,
              totalFormatted: `- ${formatCurrency(Math.abs(displayTotal))}`,
              payment_status: 'refunded',
              paymentStatusConfig: { label: 'Refunded', badgeClass: 'bg-red-100 text-red-700' },
              created_at:   returnDateVal,
              orderDateFormatted: SalesModel.formatDate(returnDateVal),
              orderTimeFormatted: SalesModel.formatTime(returnDateVal),
              isReturn:     true,
              returnData:   r
            }
          })
        } catch (e) {
          console.warn('Could not fetch returns for sales page:', e.message)
        }

        // Merge: sales first, then returns sorted by date
        const allRows = [
          ...formattedSales,
          ...returnRows
        ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at))

        setSales(allRows)

        // ── Stats (net of returns) ────────────────────────────────────────────
        const totalAmount  = formattedSales.reduce((s, sale) => s + (sale.total || 0), 0)
        const totalOrders  = formattedSales.length
        const totalReturns = returnRows.reduce((s, r) => s + Math.abs(r.total), 0)
        const netSales     = totalAmount - totalReturns
        const avgOrderValue = totalOrders > 0 ? totalAmount / totalOrders : 0

        setStats({
          totalSales: totalOrders,
          totalAmount,
          totalReturns,
          netSales,
          avgOrderValue,
          totalOrders
        })
      } else {
        setSales([])
        setStats({ totalSales: 0, totalAmount: 0, totalReturns: 0, netSales: 0, avgOrderValue: 0, totalOrders: 0 })
      }

      setError(null)
    } catch (err) {
      console.error('Error loading sales:', err)
      setError(err.message || 'Failed to load sales data')
      showToast('Failed to load sales data', 'error')
    }
  }, [userBranchId, isBranchLocked, canViewAllBranches, showToast])

  const handleRefresh = async () => {
    setRefreshing(true)
    try {
      await loadSales()
      showToast('Sales data refreshed successfully', 'success')
    } catch (err) {
      console.error('Refresh error:', err)
      showToast('Failed to refresh sales data', 'error')
    } finally {
      setRefreshing(false)
    }
  }

  const handleViewSale = (sale) => {
    setSelectedSale(sale)
    setShowViewModal(true)
  }

  const handleViewReturn = (returnRow) => {
    setSelectedReturnView(returnRow.returnData)
  }

  // Print return receipt — Courier Prime thermal style (mirrors Returns.jsx logic)
  const handlePrintReturnReceipt = useCallback((returnRow) => {
    const r           = returnRow.returnData
    if (!r) return
    const orderNumber = r.order?.orderNumber || r.orderNumber || r._id?.slice(-8) || 'N/A'
    const customer    = r.order?.customer?.name || 'Walk-in'
    const branchName  = r.order?.branch?.name || 'TEZLAA CAF\u00c9'
    const refundMeth  = (r.refundMethod || 'cash').toUpperCase()
    const reason      = (r.reasonType || '').replace(/_/g, ' ')
    const handledBy   = r.createdBy?.username || 'N/A'
    const returnDate  = new Date(r.createdAt || r.returnDate).toLocaleString('en-GB')
    const totalRefund = r.totalAmount || 0
    const items       = r.items || []
    const totalQty    = items.reduce((s, i) => s + (i.returnQuantity || 1), 0) || 1

    const itemRows = items.map(item => {
      const name = item.product?.name || 'Item'
      const qty  = item.returnQuantity || 1
      const actualUnit =
        (item.refundedAmount != null ? item.refundedAmount / qty : null) ??
        item.promotionEngineResult?.unitDetails?.finalPricePerUnit ??
        (totalQty > 0 ? totalRefund / totalQty : null) ??
        item.unitPrice ?? 0
      const lineTotal = actualUnit * qty
      return `<div style="display:flex;justify-content:space-between;font-size:13px;margin-bottom:4px;">
        <span style="flex:2;word-break:break-word;padding-right:4px;">${name}</span>
        <span style="white-space:nowrap;">${qty} x Rs.${actualUnit.toFixed(2)} = Rs.${lineTotal.toFixed(2)}</span>
      </div>`
    }).join('')

    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Return Receipt - ${orderNumber}</title>
    <style>
      @import url('https://fonts.googleapis.com/css2?family=Courier+Prime:wght@400;700&display=swap');
      * { margin:0; padding:0; box-sizing:border-box; }
      body { font-family:'Courier Prime',Courier,monospace; font-size:14px; width:80mm; margin:0 auto; padding:8px; }
      .center { text-align:center; }
      .bold { font-weight:700; }
      .divider { border-top:1px dashed #000; margin:6px 0; }
      .row { display:flex; justify-content:space-between; font-size:13px; margin-bottom:3px; }
      .total-row { display:flex; justify-content:space-between; font-weight:700; font-size:15px; margin-top:4px; border-top:1px solid #000; padding-top:4px; }
      @media print { @page { margin:0; width:80mm; } body { padding:4px; } }
    </style></head><body>
    <div class="center bold" style="font-size:16px;">${branchName}</div>
    <div class="center" style="font-size:12px;margin-bottom:4px;">RETURN RECEIPT</div>
    <div class="divider"></div>
    <div class="row"><span>Order #:</span><span>${orderNumber}</span></div>
    <div class="row"><span>Customer:</span><span>${customer}</span></div>
    <div class="row"><span>Date:</span><span>${returnDate}</span></div>
    <div class="row"><span>Reason:</span><span>${reason}</span></div>
    <div class="row"><span>Handled by:</span><span>${handledBy}</span></div>
    <div class="divider"></div>
    ${itemRows}
    <div class="divider"></div>
    <div class="total-row"><span>REFUND TOTAL</span><span>Rs.${totalRefund.toFixed(2)}</span></div>
    <div class="row" style="margin-top:4px;"><span>Method:</span><span>${refundMeth}</span></div>
    <div class="divider"></div>
    <div class="center" style="font-size:11px;margin-top:6px;">Thank you</div>
    </body></html>`

    const win = window.open('', '_blank', 'width=400,height=600')
    win.document.write(html)
    win.document.close()
    win.focus()
    win.print()
  }, [])

  const handleDownloadReturnHTML = useCallback((returnRow) => {
    const r = returnRow.returnData
    if (!r) return
    const orderNumber = r.order?.orderNumber || r.orderNumber || r._id?.slice(-8) || 'N/A'
    const customer    = r.order?.customer?.name || 'Walk-in'
    const branchName  = r.order?.branch?.name || 'TEZLAA CAF\u00c9'
    const refundMeth  = (r.refundMethod || 'cash').toUpperCase()
    const reason      = (r.reasonType || '').replace(/_/g, ' ')
    const handledBy   = r.createdBy?.username || 'N/A'
    const returnDate  = new Date(r.createdAt || r.returnDate).toLocaleString('en-GB')
    const totalRefund = r.totalAmount || 0
    const items       = r.items || []
    const totalQty    = items.reduce((s, i) => s + (i.returnQuantity || 1), 0) || 1

    const itemRows = items.map(item => {
      const name = item.product?.name || 'Item'
      const qty  = item.returnQuantity || 1
      const actualUnit =
        (item.refundedAmount != null ? item.refundedAmount / qty : null) ??
        item.promotionEngineResult?.unitDetails?.finalPricePerUnit ??
        (totalQty > 0 ? totalRefund / totalQty : null) ??
        item.unitPrice ?? 0
      const lineTotal = actualUnit * qty
      return `<div style="display:flex;justify-content:space-between;font-size:13px;margin-bottom:4px;">
        <span style="flex:2;word-break:break-word;padding-right:4px;">${name}</span>
        <span style="white-space:nowrap;">${qty} x Rs.${actualUnit.toFixed(2)} = Rs.${lineTotal.toFixed(2)}</span>
      </div>`
    }).join('')

    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Return-${orderNumber}</title>
    <style>
      @import url('https://fonts.googleapis.com/css2?family=Courier+Prime:wght@400;700&display=swap');
      * { margin:0; padding:0; box-sizing:border-box; }
      body { font-family:'Courier Prime',Courier,monospace; font-size:14px; width:80mm; margin:0 auto; padding:8px; }
      .center { text-align:center; } .bold { font-weight:700; }
      .divider { border-top:1px dashed #000; margin:6px 0; }
      .row { display:flex; justify-content:space-between; font-size:13px; margin-bottom:3px; }
      .total-row { display:flex; justify-content:space-between; font-weight:700; font-size:15px; margin-top:4px; border-top:1px solid #000; padding-top:4px; }
    </style></head><body>
    <div class="center bold" style="font-size:16px;">${branchName}</div>
    <div class="center" style="font-size:12px;margin-bottom:4px;">RETURN RECEIPT</div>
    <div class="divider"></div>
    <div class="row"><span>Order #:</span><span>${orderNumber}</span></div>
    <div class="row"><span>Customer:</span><span>${customer}</span></div>
    <div class="row"><span>Date:</span><span>${returnDate}</span></div>
    <div class="row"><span>Reason:</span><span>${reason}</span></div>
    <div class="row"><span>Handled by:</span><span>${handledBy}</span></div>
    <div class="divider"></div>
    ${itemRows}
    <div class="divider"></div>
    <div class="total-row"><span>REFUND TOTAL</span><span>Rs.${totalRefund.toFixed(2)}</span></div>
    <div class="row" style="margin-top:4px;"><span>Method:</span><span>${refundMeth}</span></div>
    </body></html>`

    const blob = new Blob([html], { type: 'text/html' })
    const url  = URL.createObjectURL(blob)
    const a    = document.createElement('a')
    a.href     = url
    a.download = `return_${orderNumber}.html`
    a.click()
    URL.revokeObjectURL(url)
    showToast('Return receipt downloaded', 'success')
  }, [showToast])

  // Download handlers for different formats
  const handleDownloadPDF = useCallback(async (sale) => {
    console.log('Download PDF clicked:', sale);
    console.log('Original sale items for PDF:', sale.items);
    const receiptId = `pdf-${sale.id || sale._id}`
    setActionLoading(prev => ({ ...prev, [receiptId]: true }))
    
    try {
      // First try simple enhancement with embedded data (faster)
      let enhancedItems = SimpleItemEnhancer.enhanceItemsWithEmbeddedData(sale.items || [])
      console.log('Simple enhanced items for PDF:', enhancedItems);
      
      // Check if we still have unknown items that need API enhancement
      const hasUnknownItems = enhancedItems.some(item => 
        item.name.includes('Item #') || item.name === 'Unknown Item'
      )
      
      if (hasUnknownItems) {
        console.log('Some items still unknown, trying API enhancement for PDF...')
        enhancedItems = await EnhancedReceiptGenerator.enhanceItemsWithProductDetails(sale.items || [])
        console.log('API enhanced items for PDF:', enhancedItems);
      }
      
      const enhancedSale = { ...sale, items: enhancedItems }
      
      ReceiptGenerator.downloadReceipt(enhancedSale, `receipt_${sale.orderNumber || 'unknown'}.pdf`)
      showToast('PDF receipt downloaded successfully', 'success')
    } catch (err) {
      console.error('Error generating PDF:', err)
      showToast('Failed to generate PDF receipt', 'error')
    } finally {
      setActionLoading(prev => ({ ...prev, [receiptId]: false }))
      setDropdownOpen(prev => ({ ...prev, [sale.id || sale._id]: false }))
    }
  }, [showToast])

  const handleDownloadHTML = useCallback(async (sale) => {
    console.log('Download HTML clicked:', sale);
    console.log('Original sale items:', sale.items);
    const receiptId = `html-${sale.id || sale._id}`
    setActionLoading(prev => ({ ...prev, [receiptId]: true }))
    
    try {
      // First try simple enhancement with embedded data (faster)
      let enhancedItems = SimpleItemEnhancer.enhanceItemsWithEmbeddedData(sale.items || [])
      console.log('Simple enhanced items:', enhancedItems);
      
      // Check if we still have unknown items that need API enhancement
      const hasUnknownItems = enhancedItems.some(item => 
        item.name.includes('Item #') || item.name === 'Unknown Item'
      )
      
      if (hasUnknownItems) {
        console.log('Some items still unknown, trying API enhancement...')
        enhancedItems = await EnhancedReceiptGenerator.enhanceItemsWithProductDetails(sale.items || [])
        console.log('API enhanced items:', enhancedItems);
      }
      
      const enhancedSale = { ...sale, items: enhancedItems }
      
      HTMLReceiptGenerator.downloadHTML(enhancedSale, `receipt_${sale.orderNumber || 'unknown'}.html`)
      showToast('HTML receipt downloaded successfully', 'success')
    } catch (err) {
      console.error('Error generating HTML:', err)
      showToast('Failed to generate HTML receipt', 'error')
    } finally {
      setActionLoading(prev => ({ ...prev, [receiptId]: false }))
      setDropdownOpen(prev => ({ ...prev, [sale.id || sale._id]: false }))
    }
  }, [showToast])

  const handlePrintReceipt = useCallback(async (sale) => {
    console.log('Print receipt clicked:', sale);
    const receiptId = `print-${sale.id || sale._id}`
    setActionLoading(prev => ({ ...prev, [receiptId]: true }))
    
    try {
      // First try simple enhancement with embedded data (faster)
      let enhancedItems = SimpleItemEnhancer.enhanceItemsWithEmbeddedData(sale.items || [])
      
      // Check if we still have unknown items that need API enhancement
      const hasUnknownItems = enhancedItems.some(item => 
        item.name.includes('Item #') || item.name === 'Unknown Item'
      )
      
      if (hasUnknownItems) {
        console.log('Some items still unknown, trying API enhancement...')
        enhancedItems = await EnhancedReceiptGenerator.enhanceItemsWithProductDetails(sale.items || [])
      }
      
      const enhancedSale = { ...sale, items: enhancedItems }
      
      HTMLReceiptGenerator.printReceipt(enhancedSale)
      showToast('Print dialog opened', 'success')
    } catch (err) {
      console.error('Error opening print dialog:', err)
      showToast('Failed to open print dialog', 'error')
    } finally {
      setActionLoading(prev => ({ ...prev, [receiptId]: false }))
      setDropdownOpen(prev => ({ ...prev, [sale.id || sale._id]: false }))
    }
  }, [showToast])

  const toggleDropdown = (saleId) => {
    setDropdownOpen(prev => ({ ...prev, [saleId]: !prev[saleId] }))
  }

  // Check if any filters are active
  const hasActiveFilters = () => {
    return searchTerm || branchFilter || orderTypeFilter || dateFromFilter
  }

  // Clear all filters
  const clearFilters = () => {
    setSearchTerm('')
    // Admin/super_admin: reset to empty (see all branches). Managers: keep their branch.
    setBranchFilter(canViewAllBranches ? '' : (userBranchId || ''))
    setOrderTypeFilter('')
    setDateFromFilter('')
  }

  useEffect(() => {
    const initialize = async () => {
      setLoading(true)
      try {
        await Promise.all([loadBranches(), loadSales()]) // Simplified call
      } catch (err) {
        console.error('Error initializing sales page:', err)
        setError('Failed to initialize sales data')
      } finally {
        setLoading(false)
      }
    }
    initialize()
  }, [loadBranches, loadSales])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.dropdown-menu')) {
        setDropdownOpen({})
      }
    }
    
    document.addEventListener('click', handleClickOutside)
    return () => document.removeEventListener('click', handleClickOutside)
  }, [])

  // Stable search input handler
  const handleSearchChange = useCallback((e) => {
    const value = e.target.value
    setSearchTerm(value)
  }, [])

  // Simple search effect with debouncing
  useEffect(() => {
    if (!loading) {
      // Clear previous timeout
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current)
      }
      
      // Set new timeout
      searchTimeoutRef.current = setTimeout(() => {
        loadSales() // Use the existing loadSales function
      }, 300)
      
      // Cleanup function
      return () => {
        if (searchTimeoutRef.current) {
          clearTimeout(searchTimeoutRef.current)
        }
      }
    }
  }, [searchTerm, branchFilter, orderTypeFilter, dateFromFilter, loading, loadSales])

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <InlineLoader />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Sales Management</h1>
        </div>
        <Button 
          onClick={handleRefresh}
          disabled={refreshing}
          className="flex items-center space-x-2"
        >
          <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
          <span>Refresh</span>
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <CardContent className="flex items-center p-6">
            <div className="flex items-center">
              <div className="p-2 rounded-lg bg-blue-100">
                <ShoppingCart className="h-6 w-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Orders</p>
                <p className="text-2xl font-bold text-gray-900">{stats.totalOrders}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="flex items-center p-6">
            <div className="flex items-center">
              <div className="p-2 rounded-lg bg-green-100">
                <DollarSign className="h-6 w-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Gross Sales</p>
                <p className="text-2xl font-bold text-gray-900">{formatCurrency(stats.totalAmount)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="flex items-center p-6">
            <div className="flex items-center">
              <div className="p-2 rounded-lg bg-red-100">
                <TrendingUp className="h-6 w-6 text-red-500" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Returns</p>
                <p className="text-2xl font-bold text-red-600">({formatCurrency(stats.totalReturns || 0)})</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="flex items-center p-6">
            <div className="flex items-center">
              <div className="p-2 rounded-lg bg-purple-100">
                <Users className="h-6 w-6 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Net Sales</p>
                <p className="text-2xl font-bold text-gray-900">{formatCurrency(stats.netSales || stats.totalAmount)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-2">
              <Filter className="h-5 w-5 text-gray-600" />
              <h3 className="text-lg font-semibold text-gray-900">Filter Units</h3>
            </div>
            {hasActiveFilters() && (
              <button
                onClick={clearFilters}
                className="flex items-center space-x-2 px-4 py-2 text-orange-500 border border-orange-300 rounded-lg hover:bg-orange-50 transition-colors"
              >
                
                <span>Clear Filters</span>
              </button>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <input
                  key="search-input"
                  type="text"
                  placeholder="Search orders..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  className="pl-10 w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-50"
                />
              </div>
            </div>

            <div>
              <div className="relative">
                <select
                  value={orderTypeFilter}
                  onChange={(e) => setOrderTypeFilter(e.target.value)}
                  className="w-full px-4 py-3 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none bg-gray-50 text-gray-700"
                >
                  <option value="">All Types</option>
                  <option value="dine-in">Dine In</option>
                  <option value="takeaway">Takeaway</option>
                  <option value="delivery">Delivery</option>
                  <option value="online">Online</option>
                </select>
                <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4 pointer-events-none" />
              </div>
            </div>

            {/* Branch dropdown: visible to admin and super_admin only */}
            {canViewAllBranches && (
              <div>
                <div className="relative">
                  <select
                    value={branchFilter}
                    onChange={(e) => setBranchFilter(e.target.value)}
                    className="w-full px-4 py-3 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none bg-gray-50 text-gray-700"
                  >
                    <option value="">All Branches</option>
                    {branches.map((branch) => (
                      <option key={branch.id || branch._id} value={branch.id || branch._id}>
                        {branch.name}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4 pointer-events-none" />
                </div>
              </div>
            )}

            {/* Locked branch badge for managers/cashiers */}
            {isBranchLocked && (
              <div className="flex items-center px-4 py-3 bg-orange-50 border border-orange-200 rounded-lg">
                <Globe className="h-4 w-4 text-orange-500 mr-2 flex-shrink-0" />
                <span className="text-sm text-orange-700 font-medium truncate">
                  {branches.find(b => (b._id || b.id) === userBranchId)?.name || 'Your Branch'}
                </span>
              </div>
            )}

            <div>
              <input
                type="date"
                value={dateFromFilter}
                onChange={(e) => setDateFromFilter(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-50"
              />
            </div>
          </div>

          {sales.length > 0 && (
            <div className="mt-4 text-sm text-gray-600">
              Showing {sales.length} of {sales.length} units
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {error ? (
            <div className="flex items-center justify-center py-12 text-red-600">
              <AlertTriangle className="h-5 w-5 mr-2" />
              <span>{error}</span>
            </div>
          ) : sales.length === 0 ? (
            <div className="p-8 text-center">
              <DollarSign className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">
                {hasActiveFilters() ? 'No sales match your filters' : 'No sales found'}
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                {hasActiveFilters() 
                  ? 'Try adjusting your search criteria or clearing filters.'
                  : 'No completed orders found matching your criteria.'
                }
              </p>
              {hasActiveFilters() && (
                <button
                  onClick={clearFilters}
                  className="mt-3 inline-flex items-center px-3 py-2 text-sm text-orange-600 hover:text-orange-800"
                >
                  Clear all filters
                </button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Order
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Customer
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Items
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Discount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Total
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Payment
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {(() => {
                    const totalPages = Math.max(1, Math.ceil(sales.length / itemsPerPage))
                    const startIndex = (currentPage - 1) * itemsPerPage
                    const paginatedSales = sales.slice(startIndex, startIndex + itemsPerPage)

                    return paginatedSales.map((sale) => (
                      <tr
                        key={sale.id || sale._id}
                        className={`hover:bg-gray-50 ${sale.isReturn ? 'bg-red-50 border-l-4 border-red-400' : ''}`}
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              #{sale.orderNumber}
                            </div>
                            <div className="text-sm text-gray-500">
                              {sale.branchName}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {sale.customerName}
                            </div>
                            {sale.customerMobile && (
                              <div className="text-sm text-gray-500">
                                {sale.customerMobile}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${sale.orderTypeConfig?.badgeClass || 'bg-gray-100 text-gray-800'}`}>
                            {sale.orderTypeConfig?.label || sale.orderType}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {sale.isReturn ? `${sale.itemsCount} returned` : `${sale.itemsCount} items`}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {!sale.isReturn && sale.discount > 0 ? (
                              <span className="text-green-600 font-medium">
                                - {sale.discountFormatted || formatCurrency(sale.discount || 0)}
                              </span>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {sale.isReturn ? (
                            <div className="text-sm font-bold text-red-600">
                              {sale.totalFormatted}
                            </div>
                          ) : (
                            <div className="text-sm font-medium text-gray-900">
                              {sale.totalFormatted || formatCurrency(sale.total || 0)}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${sale.paymentStatusConfig?.badgeClass || 'bg-green-100 text-green-800'}`}>
                            {sale.paymentStatusConfig?.label || sale.payment_status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <Calendar className="h-4 w-4 text-gray-400 mr-2" />
                            <div>
                              <div className="text-sm text-gray-900">
                                {sale.orderDateFormatted || new Date(sale.created_at).toLocaleDateString()}
                              </div>
                              <div className="text-sm text-gray-500">
                                {sale.orderTimeFormatted || new Date(sale.created_at).toLocaleTimeString()}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          {sale.isReturn ? (
                            <div className="flex items-center justify-center space-x-2">
                              {/* View return details */}
                              <button
                                onClick={() => handleViewReturn(sale)}
                                className="p-2 text-gray-400 hover:text-blue-500"
                                title="View Return Details"
                              >
                                <Eye className="h-4 w-4" />
                              </button>

                              {/* Return receipt actions dropdown */}
                              <div className="relative dropdown-menu">
                                <button
                                  onClick={() => toggleDropdown(sale.id || sale._id)}
                                  className="p-2 text-red-400 hover:text-red-600 flex items-center"
                                  title="Return Receipt Options"
                                >
                                  <Download className="h-4 w-4" />
                                  <ChevronDown className="h-3 w-3 ml-1" />
                                </button>

                                {dropdownOpen[sale.id || sale._id] && (
                                  <div className="absolute right-0 mt-2 w-52 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
                                    <div className="py-1">
                                      <button
                                        onClick={() => { handlePrintReturnReceipt(sale); toggleDropdown(sale.id || sale._id) }}
                                        className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                                      >
                                        <Printer className="h-4 w-4 text-orange-500 mr-3" />
                                        Print Return Receipt
                                      </button>
                                      <button
                                        onClick={() => { handleDownloadReturnHTML(sale); toggleDropdown(sale.id || sale._id) }}
                                        className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                                      >
                                        <Globe className="h-4 w-4 text-blue-500 mr-3" />
                                        Download HTML
                                      </button>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          ) : (
                            <div className="flex items-center justify-center space-x-2">
                              <button
                                onClick={() => handleViewSale(sale)}
                                className="p-2 text-gray-400 hover:text-gray-500 "
                                title="View Details"
                              >
                                <Eye className="h-4 w-4" />
                              </button>

                              {/* Download Dropdown */}
                              <div className="relative dropdown-menu">
                                <button
                                  onClick={() => toggleDropdown(sale.id || sale._id)}
                                  className="p-2 text-green-500 hover:text-green-700  flex items-center"
                                  title="Download Options"
                                >
                                  <Download className="h-4 w-4" />
                                  <ChevronDown className="h-3 w-3 ml-1" />
                                </button>

                                {dropdownOpen[sale.id || sale._id] && (
                                  <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
                                    <div className="py-1">
                                      <button
                                        onClick={() => handleDownloadPDF(sale)}
                                        className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                                        disabled={actionLoading[`pdf-${sale.id || sale._id}`]}
                                      >
                                        {actionLoading[`pdf-${sale.id || sale._id}`] ? (
                                          <div className="animate-spin h-4 w-4 border-2 border-red-500 border-t-transparent rounded-full mr-3"></div>
                                        ) : (
                                          <FileText className="h-4 w-4 text-red-500 mr-3" />
                                        )}
                                        Download PDF
                                      </button>

                                      <button
                                        onClick={() => handleDownloadHTML(sale)}
                                        className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                                        disabled={actionLoading[`html-${sale.id || sale._id}`]}
                                      >
                                        {actionLoading[`html-${sale.id || sale._id}`] ? (
                                          <div className="animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full mr-3"></div>
                                        ) : (
                                          <Globe className="h-4 w-4 text-blue-500 mr-3" />
                                        )}
                                        Download HTML
                                      </button>

                                      <button
                                        onClick={() => handlePrintReceipt(sale)}
                                        className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                                        disabled={actionLoading[`print-${sale.id || sale._id}`]}
                                      >
                                        {actionLoading[`print-${sale.id || sale._id}`] ? (
                                          <div className="animate-spin h-4 w-4 border-2 border-green-500 border-t-transparent rounded-full mr-3"></div>
                                        ) : (
                                          <Printer className="h-4 w-4 text-green-500 mr-3" />
                                        )}
                                        Print Receipt
                                      </button>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))
                  })()}
                </tbody>
              </table>
            </div>
          )}

          {/* Page Breakdown / Pagination Controls */}
          {sales.length > 0 && (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 px-6 py-4 bg-gray-50 border-t border-gray-200">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <span>Show</span>
                <select
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value))
                    setCurrentPage(1)
                  }}
                  className="px-2.5 py-1 border border-gray-300 rounded-lg bg-white font-semibold text-gray-800 focus:ring-2 focus:ring-orange-500 focus:outline-none text-xs shadow-sm"
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
                <span>entries per page</span>
              </div>

              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500 mr-2">
                  Showing {sales.length === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, sales.length)} of {sales.length} entries
                </span>
                <div className="inline-flex items-center space-x-1">
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1.5 border border-gray-300 rounded-lg bg-white hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed text-xs font-semibold text-gray-700 transition-colors shadow-sm"
                  >
                    Previous
                  </button>

                  {Array.from({ length: Math.min(5, Math.max(1, Math.ceil(sales.length / itemsPerPage))) }, (_, i) => {
                    const totalPages = Math.max(1, Math.ceil(sales.length / itemsPerPage))
                    let pNum = currentPage
                    if (totalPages <= 5) pNum = i + 1
                    else if (currentPage <= 3) pNum = i + 1
                    else if (currentPage >= totalPages - 2) pNum = totalPages - 4 + i
                    else pNum = currentPage - 2 + i

                    return (
                      <button
                        key={pNum}
                        onClick={() => setCurrentPage(pNum)}
                        className={`px-3 py-1.5 border rounded-lg text-xs font-bold transition-all shadow-sm ${
                          currentPage === pNum
                            ? 'bg-orange-500 text-white border-orange-500'
                            : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-100'
                        }`}
                      >
                        {pNum}
                      </button>
                    )
                  })}

                  <button
                    onClick={() => setCurrentPage((p) => Math.min(Math.ceil(sales.length / itemsPerPage), p + 1))}
                    disabled={currentPage >= Math.ceil(sales.length / itemsPerPage)}
                    className="px-3 py-1.5 border border-gray-300 rounded-lg bg-white hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed text-xs font-semibold text-gray-700 transition-colors shadow-sm"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {showViewModal && selectedSale && (
        <ViewSaleModal
          isOpen={showViewModal}
          sale={selectedSale}
          onClose={() => {
            setShowViewModal(false)
            setSelectedSale(null)
          }}
        />
      )}

      {/* Return Details Modal */}
      {selectedReturnView && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setSelectedReturnView(null)}>
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div className="flex items-center justify-between p-6 bg-orange-500 text-white rounded-t-xl">
              <div>
                <h2 className="text-xl font-bold">Return Details</h2>
                <p className="text-sm text-orange-100">Order #{selectedReturnView.order?.orderNumber || selectedReturnView._id?.slice(-8)}</p>
              </div>
              <button onClick={() => setSelectedReturnView(null)} className="p-2 hover:bg-orange-600 rounded-lg transition-colors">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6 space-y-5">
              {/* Order info */}
              <div>
                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Order Information</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div><p className="text-xs text-gray-500">Order Number</p><p className="font-semibold">{selectedReturnView.order?.orderNumber || 'N/A'}</p></div>
                  <div><p className="text-xs text-gray-500">Customer</p><p className="font-semibold">{selectedReturnView.order?.customer?.name || 'Walk-in'}</p></div>
                  <div><p className="text-xs text-gray-500">Branch</p><p className="font-semibold">{selectedReturnView.order?.branch?.name || 'N/A'}</p></div>
                  <div><p className="text-xs text-gray-500">Original Amount</p><p className="font-semibold">{formatCurrency(selectedReturnView.order?.orderPayment?.totalAmount || 0)}</p></div>
                </div>
              </div>

              {/* Return info */}
              <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
                <h3 className="text-sm font-semibold text-orange-700 mb-3">Return Information</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div><p className="text-xs text-gray-500">Return Date</p><p className="font-semibold">{new Date(selectedReturnView.createdAt || selectedReturnView.returnDate).toLocaleString('en-GB')}</p></div>
                  <div><p className="text-xs text-gray-500">Refund Amount</p><p className="font-bold text-orange-700">{formatCurrency(selectedReturnView.totalAmount || 0)}</p></div>
                  <div><p className="text-xs text-gray-500">Return Type</p><p className="font-semibold capitalize">{selectedReturnView.returnType || 'N/A'}</p></div>
                  <div><p className="text-xs text-gray-500">Refund Method</p><p className="font-semibold capitalize">{selectedReturnView.refundMethod || 'N/A'}</p></div>
                  <div><p className="text-xs text-gray-500">Reason</p><p className="font-semibold capitalize">{(selectedReturnView.reasonType || '').replace(/_/g, ' ') || 'N/A'}</p></div>
                  <div><p className="text-xs text-gray-500">Processed by</p><p className="font-semibold">{selectedReturnView.createdBy?.username || 'System'}</p></div>
                </div>
              </div>

              {/* Returned items */}
              <div>
                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Returned Items</h3>
                <div className="space-y-2">
                  {(() => {
                    const totalQty = selectedReturnView.items?.reduce((s, i) => s + (i.returnQuantity || 1), 0) || 1
                    const totalRefund = selectedReturnView.totalAmount || 0
                    return selectedReturnView.items?.map((item, idx) => {
                      const qty = item.returnQuantity || 1
                      const actualUnit =
                        (item.refundedAmount != null ? item.refundedAmount / qty : null) ??
                        item.promotionEngineResult?.unitDetails?.finalPricePerUnit ??
                        (totalQty > 0 ? totalRefund / totalQty : null) ??
                        item.unitPrice ?? 0
                      const originalPrice = item.unitPrice || 0
                      const hasDiscount = originalPrice > 0 && Math.abs(originalPrice - actualUnit) > 0.01
                      return (
                        <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded border">
                          <div>
                            <p className="font-medium text-sm">{item.product?.name || 'Unknown'}</p>
                            <p className="text-xs text-gray-500">SKU: {item.product?.sku || 'N/A'} | Qty: {qty}</p>
                            {hasDiscount && <p className="text-xs text-gray-400 line-through">Original: {formatCurrency(originalPrice)}</p>}
                          </div>
                          <div className="text-right">
                            <p className="font-semibold text-orange-700">{formatCurrency(actualUnit)}/unit</p>
                            {qty > 1 && <p className="text-xs text-gray-500">= {formatCurrency(actualUnit * qty)}</p>}
                          </div>
                        </div>
                      )
                    })
                  })()}
                </div>
              </div>
            </div>

            {/* Footer actions */}
            <div className="flex items-center justify-end gap-3 p-4 border-t bg-gray-50 rounded-b-xl">
              <button
                onClick={() => handleDownloadReturnHTML({ returnData: selectedReturnView })}
                className="flex items-center gap-2 px-4 py-2 text-sm text-blue-600 border border-blue-300 rounded-lg hover:bg-blue-50 transition-colors"
              >
                <Globe className="h-4 w-4" />
                Download HTML
              </button>
              <button
                onClick={() => handlePrintReturnReceipt({ returnData: selectedReturnView })}
                className="flex items-center gap-2 px-4 py-2 text-sm text-white bg-orange-500 rounded-lg hover:bg-orange-600 transition-colors"
              >
                <Printer className="h-4 w-4" />
                Print Receipt
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Sales
